import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaUsers, FaSearch, FaTimes, FaSort, FaSortUp, FaSortDown, FaUserCheck, FaUserPlus, FaForward, FaUndo, FaChevronDown, FaHistory, FaClipboardCheck } from 'react-icons/fa';
import './TeamsManager.css';
import API_BASE_URL from '../../config/api';
import TeamAttendanceHistoryModal from '../Attendance/TeamAttendanceHistoryModal';

/* ── Sport Emoji Helper ──────────────────────────────────── */
const getSportEmoji = (sportName) => {
    const name = (sportName || '').toLowerCase();
    if (name.includes('atletism') || name.includes('atletica')) return '🏃';
    if (name.includes('badminton')) return '🏸';
    if (name.includes('basquet') || name.includes('basket')) return '🏀';
    if (name.includes('futvolei') || name.includes('footvolley') || name.includes('futvolley')) return '🏖️';
    if (name.includes('futsal')) return '⚽';
    if (name.includes('hóquei') || name.includes('hockey') || name.includes('patins')) return '🛼';
    if (name.includes('tenis') || name.includes('ténis') || name.includes('mesa')) return '🏓';
    if (name.includes('volei') || name.includes('volley')) return '🏐';
    return '🏆';
};

/* ── Team Card ───────────────────────────────────────────── */
const TeamCard = ({ team, onView, onEdit, onDelete, onAdvance, onRevert, onAttendance, getGenderBadgeClass, getGenderLabel }) => (
    <div className="team-card">
        <div className="team-card-body">
            <h3 className="team-card-name">{team.name}</h3>
            <div className="team-card-badges">
                {team.category && (
                    <span className="team-card-escalao-badge">{team.category}</span>
                )}
                <span className={`gender-badge ${getGenderBadgeClass(team.gender)}`}>
                    {getGenderLabel(team.gender)}
                </span>
            </div>
        </div>
        <div className="team-card-footer">
            <div className="team-card-actions">
                {team.isActive ? (
                    onAdvance && (
                        <button className="teams-action-btn next-season" onClick={() => onAdvance(team)} title="Avançar Época desta equipa">
                            <FaForward />
                        </button>
                    )
                ) : (
                    onRevert && (
                        <button className="teams-action-btn revert" onClick={() => onRevert(team)} title="Reverter Época desta equipa">
                            <FaUndo />
                        </button>
                    )
                )}
                <button className="teams-action-btn view" onClick={() => onView(team.id)} title="Ver equipa">
                    <FaUsers />
                </button>
                {onAttendance && (
                    <button className="teams-action-btn attendance" onClick={() => onAttendance(team)} title="Quadro de Presenças">
                        <FaClipboardCheck />
                    </button>
                )}
                <button className="teams-action-btn edit" onClick={() => onEdit(team)} title="Editar">
                    <FaEdit />
                </button>
                <button className="teams-action-btn delete" onClick={() => onDelete(team.id)} title="Eliminar">
                    <FaTrash />
                </button>
            </div>
        </div>
    </div>
);

/* ── Searchable Escalão Combobox ─────────────────────────── */
const EscalaoSearchSelect = ({ escalaos, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef(null);

    const selected = escalaos.find(e => String(e.id) === String(value));

    useEffect(() => {
        if (!open) setQuery(selected ? selected.name : '');
    }, [value, open]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setQuery(selected ? selected.name : '');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [selected]);

    const filtered = query.trim()
        ? escalaos.filter(e => e.name.toLowerCase().includes(query.toLowerCase()))
        : escalaos;

    const handleSelect = (esc) => {
        onChange(esc ? String(esc.id) : '');
        setQuery(esc ? esc.name : '');
        setOpen(false);
    };

    return (
        <div className="escalao-combobox" ref={containerRef}>
            <div className="escalao-combobox-input-wrap">
                <input
                    type="text"
                    className="teams-form-input"
                    placeholder="Pesquisar escalão..."
                    value={open ? query : (selected ? selected.name : '')}
                    onFocus={() => { setOpen(true); setQuery(''); }}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    autoComplete="off"
                />
                {value && (
                    <button type="button" className="escalao-combobox-clear" onClick={() => handleSelect(null)} title="Limpar">×</button>
                )}
            </div>
            {open && (
                <ul className="escalao-combobox-list">
                    <li
                        className={`escalao-combobox-item escalao-combobox-empty-opt${!value ? ' selected' : ''}`}
                        onMouseDown={() => handleSelect(null)}
                    >
                        Sem escalão
                    </li>
                    {filtered.length === 0 && (
                        <li className="escalao-combobox-no-results">Sem resultados</li>
                    )}
                    {filtered.map(esc => (
                        <li
                            key={esc.id}
                            className={`escalao-combobox-item${String(esc.id) === String(value) ? ' selected' : ''}`}
                            onMouseDown={() => handleSelect(esc)}
                        >
                            {esc.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/* ── Main Component ──────────────────────────────────────── */
const TeamsManager = ({ isCoordinator = false, coordinatorSportId = null }) => {
    const [teams, setTeams] = useState([]);
    const [sports, setSports] = useState([]);
    const [escalaos, setEscalaos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [viewingTeam, setViewingTeam] = useState(null);
    const [addingAthletes, setAddingAthletes] = useState(false);
    const [availableAthletes, setAvailableAthletes] = useState([]);
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [athletesLoading, setAthletesLoading] = useState(false);
    const [athleteSearch, setAthleteSearch] = useState('');

    // Coach related state
    const [addingCoaches, setAddingCoaches] = useState(false);
    const [availableCoaches, setAvailableCoaches] = useState([]);
    const [selectedCoaches, setSelectedCoaches] = useState([]);
    const [coachesLoading, setCoachesLoading] = useState(false);
    const [coachSearch, setCoachSearch] = useState('');

    const [selectedSportFilter, setSelectedSportFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [expandedSportHistory, setExpandedSportHistory] = useState({});

    // Attendance modal state
    const [attendanceTeam, setAttendanceTeam] = useState(null);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);

    const handleOpenAttendance = async (team) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/${team.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAttendanceTeam(data);
                setShowAttendanceModal(true);
                return;
            }
        } catch (err) {
            console.error('Error fetching team for attendance:', err);
        }
        setAttendanceTeam(team);
        setShowAttendanceModal(true);
    };
    const [expandedHistorySeasons, setExpandedHistorySeasons] = useState({});
    const [expandedCurrentSeasons, setExpandedCurrentSeasons] = useState({});

    // Season Advance state (by sport)
    const [showSeasonAdvanceModal, setShowSeasonAdvanceModal] = useState(false);
    const [advanceSportId, setAdvanceSportId] = useState(null);
    const [advanceTeamsData, setAdvanceTeamsData] = useState([]); // [{teamId, teamName, athletes:[]}]
    const [advanceAllTeams, setAdvanceAllTeams] = useState([]);   // all active teams of the sport
    const [advanceLoading, setAdvanceLoading] = useState(false);
    const [advanceSubmitting, setAdvanceSubmitting] = useState(false);
    // promoted: { [athleteProfileId]: { fromTeamId, destinationTeamId } }
    const [promotedAthletes, setPromotedAthletes] = useState({});
    // Server-side promotion suggestions: { [athleteProfileId]: previewItem }
    const [advancePromotionData, setAdvancePromotionData] = useState({});

    // Single Team Season Advance state
    const [showTeamAdvanceModal, setShowTeamAdvanceModal] = useState(false);
    const [advanceTeam, setAdvanceTeam] = useState(null);
    const [advanceAthletes, setAdvanceAthletes] = useState([]);
    const [advanceEscalaos, setAdvanceEscalaos] = useState([]);
    const [advanceSportTeams, setAdvanceSportTeams] = useState([]);
    const [teamPromotions, setTeamPromotions] = useState({});

    const initialFormState = {
        sportId: '',
        name: '',
        escalaoId: '',
        gender: 2,
        season: `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`,
        isActive: true,
        jerseyNumberMin: 0,
        jerseyNumberMax: 99
    };
    const [formData, setFormData] = useState(initialFormState);

    const getEmailBase = (email) => {
        if (!email) return '';
        const [localPart, domain] = email.split('@');
        if (!localPart || !domain) return email;
        const [base] = localPart.split('+');
        return `${base}@${domain}`;
    };

    useEffect(() => {
        fetchTeams();
        fetchSports();
        fetchEscalaos();
    }, [isCoordinator, coordinatorSportId]);

    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                let filtered = data;
                if (isCoordinator && coordinatorSportId) {
                    filtered = filtered.filter(t => t.sportId === parseInt(coordinatorSportId));
                }
                setTeams(filtered);
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSports = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                let activeSports = data.filter(s => s.isActive);
                if (isCoordinator && coordinatorSportId) {
                    activeSports = activeSports.filter(s => s.id === parseInt(coordinatorSportId));
                }
                setSports(activeSports);
            }
        } catch (error) {
            console.error('Error fetching sports:', error);
        }
    };

    const fetchEscalaos = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/escalaos/all`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setEscalaos(data);
            }
        } catch (error) {
            console.error('Error fetching escalaos:', error);
        }
    };

    const fetchTeamDetails = async (teamId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setViewingTeam(data);
                setShowDetailsModal(true);
            }
        } catch (error) {
            console.error('Error fetching team details:', error);
            alert('Erro ao carregar detalhes da equipa');
        }
    };

    const fetchAvailableAthletes = async () => {
        setAthletesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users?profileType=athlete&isActive=true&pageSize=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableAthletes(Array.isArray(data) ? data : (data.items ?? []));
            }
        } catch (error) {
            console.error('Error fetching athletes:', error);
        } finally {
            setAthletesLoading(false);
        }
    };

    const fetchAvailableCoaches = async () => {
        setCoachesLoading(true);
        try {
            const token = localStorage.getItem('token');
            // Check api. Ensure the profile type is 'coach'
            const response = await fetch(`${API_BASE_URL}/api/users?profileType=coach&isActive=true&pageSize=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableCoaches(Array.isArray(data) ? data : (data.items ?? []));
            }
        } catch (error) {
            console.error('Error fetching coaches:', error);
        } finally {
            setCoachesLoading(false);
        }
    };

    const generateTeamName = (data) => {
        const sport = sports.find(s => s.id === parseInt(data.sportId));
        const escalao = escalaos.find(e => e.id === parseInt(data.escalaoId));
        if (!sport) return data.name;
        let name = sport.name;
        if (escalao) name += ` ${escalao.name}`;
        if (parseInt(data.gender) === 0) name += ' (masculino)';
        if (parseInt(data.gender) === 1) name += ' (masculino)';
        if (parseInt(data.gender) === 2) name += ' (feminino)';
        if (parseInt(data.gender) === 3) name += ' (mista)';
        return name;
    };

    const updateFormField = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'sportId' || field === 'escalaoId' || field === 'gender') {
                updated.name = generateTeamName(updated);
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const token = localStorage.getItem('token');
        const dataToSend = {
            sportId: parseInt(formData.sportId),
            name: formData.name,
            escalaoId: formData.escalaoId ? parseInt(formData.escalaoId) : null,
            gender: parseInt(formData.gender),
            season: formData.season || null,
            isActive: formData.isActive,
            jerseyNumberMin: formData.jerseyNumberMin !== '' ? parseInt(formData.jerseyNumberMin) : null,
            jerseyNumberMax: formData.jerseyNumberMax !== '' ? parseInt(formData.jerseyNumberMax) : null
        };
        try {
            const url = editingTeam
                ? `${API_BASE_URL}/api/teams/${editingTeam.id}`
                : `${API_BASE_URL}/api/teams`;
            const response = await fetch(url, {
                method: editingTeam ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(dataToSend)
            });
            if (response.ok) {
                await fetchTeams();
                setShowModal(false);
                resetForm();
                alert(editingTeam ? 'Equipa atualizada com sucesso!' : 'Equipa criada com sucesso!');
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao guardar equipa');
            }
        } catch (error) {
            console.error('Error saving team:', error);
            alert('Erro ao guardar equipa');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveAthlete = async (athleteProfileId) => {
        if (!window.confirm('Tem a certeza que deseja remover este atleta da equipa?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${viewingTeam.id}/athletes/${athleteProfileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Atleta removido com sucesso!');
                await fetchTeamDetails(viewingTeam.id);
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao remover atleta');
            }
        } catch (error) {
            console.error('Error removing athlete:', error);
            alert('Erro ao remover atleta');
        }
    };

    const handleRemoveCoach = async (coachId) => {
        if (!window.confirm('Tem a certeza que deseja remover este treinador da equipa?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${viewingTeam.id}/coaches/${coachId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Treinador removido com sucesso!');
                await fetchTeamDetails(viewingTeam.id);
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao remover treinador');
            }
        } catch (error) {
            console.error('Error removing coach:', error);
            alert('Erro ao remover treinador');
        }
    };

    // Deduz o género "intrínseco" de um escalão pelo nome (ex: "Juvenis Femininos", "Sub 16 Masc")
    const getEscalaoNameGender = (escalaoName) => {
        const name = (escalaoName || '').toLowerCase();
        const hasMasc = name.includes('masculin') || name.includes('masc');
        const hasFem = name.includes('feminin') || name.includes('fem');
        if (hasMasc && !hasFem) return 1;
        if (hasFem && !hasMasc) return 2;
        if (name.includes('misto') || name.includes('mista')) return 3;
        return null;
    };

    // Determina se o escalão é compatível com o género da equipa
    const escalaoMatchesTeamGender = (escalao, teamOrGender, sportTeams) => {
        const teamGen = typeof teamOrGender === 'object' ? teamOrGender?.gender : teamOrGender;
        if (teamGen !== 1 && teamGen !== 2) return true;

        const escName = typeof escalao === 'object' ? escalao?.name : escalao;
        const escId = typeof escalao === 'object' ? escalao?.id : null;
        const escGender = getEscalaoNameGender(escName);

        if (escGender != null) {
            return escGender === teamGen;
        }

        if (escId && sportTeams && sportTeams.length > 0) {
            const teamsInThisEsc = sportTeams.filter(t => t.escalaoId === escId);
            if (teamsInThisEsc.length > 0) {
                const hasTargetTeam = teamsInThisEsc.some(t => t.gender === teamGen);
                const hasOppositeTeam = teamsInThisEsc.some(t =>
                    (teamGen === 1 && t.gender === 2) ||
                    (teamGen === 2 && t.gender === 1)
                );
                if (hasOppositeTeam && !hasTargetTeam) return false;
            }
        }

        return true;
    };

    // Se um atleta está numa equipa masculina/feminina, assume-se o género da equipa.
    // Equipas mistas mantêm o valor do atleta.
    const getEffectiveAthleteGender = (athleteOrGender, teamOrGender) => {
        const teamGen = typeof teamOrGender === 'object' ? teamOrGender?.gender : teamOrGender;
        const athGen = typeof athleteOrGender === 'object' ? athleteOrGender?.gender : athleteOrGender;

        if (teamGen === 1) return 1; // Masculino
        if (teamGen === 2) return 2; // Feminino
        return athGen || 3; // Misto
    };

    const getSuggestedEscalaoId = (athlete, team, escalaos, sportTeams) => {
        const targetGender = getEffectiveAthleteGender(athlete, team);

        if (athlete.suggestedEscalaoId) {
            const currentSuggested = (escalaos || []).find(e => e.id === athlete.suggestedEscalaoId);
            if (currentSuggested && escalaoMatchesTeamGender(currentSuggested, targetGender, sportTeams)) {
                return athlete.suggestedEscalaoId;
            }
        }
        if (!athlete.age || !escalaos || escalaos.length === 0) return null;

        const scoreEscalao = (esc) => {
            const nameLower = (esc.name || '').toLowerCase();
            const nameGen = getEscalaoNameGender(esc.name);
            const teamsInThisEsc = (sportTeams || []).filter(t => t.escalaoId === esc.id);
            const hasTargetTeam = teamsInThisEsc.some(t => t.gender === targetGender);
            const hasOppositeTeam = teamsInThisEsc.some(t =>
                (targetGender === 1 && t.gender === 2) ||
                (targetGender === 2 && t.gender === 1)
            );

            if (targetGender === 1) {
                if (hasTargetTeam && nameGen === 1) return 100;
                if (hasTargetTeam) return 80;
                if (nameGen === 1) return 60;
                if (nameGen !== 2 && !hasOppositeTeam) return 40;
                return 0;
            } else if (targetGender === 2) {
                if (hasTargetTeam && nameGen === 2) return 100;
                if (hasTargetTeam) return 80;
                if (nameGen === 2) return 60;
                if (nameGen !== 1 && !hasOppositeTeam) return 40;
                return 0;
            } else {
                if (nameLower.includes('misto') || nameLower.includes('mista')) return 100;
                if (hasTargetTeam) return 80;
                return 50;
            }
        };

        const matchingGenderEscalaos = (escalaos || []).filter(e => escalaoMatchesTeamGender(e, targetGender, sportTeams));
        const pool = matchingGenderEscalaos.length > 0 ? matchingGenderEscalaos : escalaos;

        const exactMatches = pool.filter(e =>
            e.id !== team?.escalaoId &&
            (e.minAge == null || athlete.age >= e.minAge) &&
            (e.maxAge == null || athlete.age <= e.maxAge)
        );
        if (exactMatches.length > 0) {
            exactMatches.sort((a, b) => scoreEscalao(b) - scoreEscalao(a));
            return exactMatches[0].id;
        }

        const higher = pool
            .filter(e => e.id !== team?.escalaoId && e.minAge != null && e.minAge >= athlete.age)
            .sort((a, b) => {
                const ageDiff = (a.minAge || 0) - (b.minAge || 0);
                if (ageDiff !== 0) return ageDiff;
                return scoreEscalao(b) - scoreEscalao(a);
            });
        if (higher.length > 0) return higher[0].id;

        const otherEscalaos = pool.filter(e => e.id !== team?.escalaoId);
        if (otherEscalaos.length > 0) {
            otherEscalaos.sort((a, b) => scoreEscalao(b) - scoreEscalao(a));
            return otherEscalaos[0].id;
        }

        return null;
    };

    const uniqueSportTeams = useMemo(() => {
        const map = new Map();
        (advanceSportTeams || []).forEach(t => {
            const key = `${t.escalaoId}_${t.name.trim().toLowerCase()}_${t.gender}`;
            if (!map.has(key)) {
                map.set(key, t);
            }
        });
        return Array.from(map.values());
    }, [advanceSportTeams]);

    const getSelectValue = (promoInfo, athlete, teams, sourceTeam) => {
        if (!promoInfo) return '';
        if (promoInfo.destinationTeamId) {
            return `team_${promoInfo.destinationTeamId}`;
        }
        if (promoInfo.destinationEscalaoId) {
            const targetGender = promoInfo.destinationGender || getEffectiveAthleteGender(athlete, sourceTeam);
            const matchingTeam = (teams || []).find(t =>
                t.escalaoId === promoInfo.destinationEscalaoId &&
                (t.gender === targetGender || (t.gender === 1 && targetGender === 1) || (t.gender === 2 && targetGender === 2))
            );
            if (matchingTeam) {
                return `team_${matchingTeam.id}`;
            }
            if (targetGender === 3) {
                const mixedTeam = (teams || []).find(t => t.escalaoId === promoInfo.destinationEscalaoId && t.gender === 3);
                if (mixedTeam) return `team_${mixedTeam.id}`;
            }
            return `escalao_${promoInfo.destinationEscalaoId}`;
        }
        return '';
    };

    const resolveSuggestedPromotion = (athlete, sourceTeam, escalaos, sportTeams) => {
        const effectiveGender = getEffectiveAthleteGender(athlete, sourceTeam);
        const escId = getSuggestedEscalaoId(athlete, sourceTeam, escalaos, sportTeams);
        if (!escId) return null;

        const defaultGender = effectiveGender;

        const teamsInEsc = (sportTeams || []).filter(t => t.escalaoId === escId);
        let matchingTeam = teamsInEsc.find(t => t.gender === defaultGender);
        if (!matchingTeam && defaultGender !== 3) {
            matchingTeam = teamsInEsc.find(t => t.gender === 3);
        }
        if (!matchingTeam && sourceTeam?.gender === 3) {
            matchingTeam = teamsInEsc.find(t => t.gender === 3) || teamsInEsc[0];
        }

        if (matchingTeam) {
            return {
                destinationEscalaoId: escId,
                destinationTeamId: matchingTeam.id,
                destinationGender: matchingTeam.gender
            };
        }

        return {
            destinationEscalaoId: escId,
            destinationTeamId: null,
            destinationGender: defaultGender
        };
    };

    /* ── Single Team Season Advance ─────────────────────────── */
    const openTeamAdvanceModal = async (team) => {
        setAdvanceTeam(team);
        setAdvanceLoading(true);
        setTeamPromotions({});
        setShowTeamAdvanceModal(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/${team.id}/athletes-for-advance`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAdvanceTeam(data.team);
                setAdvanceAthletes(data.athletes || []);
                setAdvanceEscalaos(data.escalaos || []);
                setAdvanceSportTeams(data.sportTeams || []);

                // Pre-select suggested promotions for athletes who exceed age limit
                const initialPromos = {};
                (data.athletes || []).forEach(a => {
                    if (a.shouldPromote) {
                        const promo = resolveSuggestedPromotion(a, data.team, data.escalaos, data.sportTeams);
                        if (promo) {
                            initialPromos[a.athleteProfileId] = promo;
                        }
                    }
                });
                setTeamPromotions(initialPromos);
            } else {
                alert('Erro ao carregar dados da equipa');
                setShowTeamAdvanceModal(false);
            }
        } catch (err) {
            console.error('Error loading team advance data:', err);
            alert('Erro de ligação ao carregar dados');
            setShowTeamAdvanceModal(false);
        } finally {
            setAdvanceLoading(false);
        }
    };

    const togglePromoteSingleAthlete = (athlete) => {
        setTeamPromotions(prev => {
            if (prev[athlete.athleteProfileId]) {
                const next = { ...prev };
                delete next[athlete.athleteProfileId];
                return next;
            }
            const promo = resolveSuggestedPromotion(athlete, advanceTeam, advanceEscalaos, advanceSportTeams);
            if (!promo) return prev;
            return {
                ...prev,
                [athlete.athleteProfileId]: promo
            };
        });
    };

    const setSingleAthleteDestination = (athleteProfileId, escalaoId, teamId, gender) => {
        setTeamPromotions(prev => ({
            ...prev,
            [athleteProfileId]: {
                destinationEscalaoId: escalaoId ? parseInt(escalaoId) : null,
                destinationTeamId: teamId ? parseInt(teamId) : null,
                destinationGender: gender ? parseInt(gender) : null
            }
        }));
    };

    const handleSelectSuggestedSingleAdvance = () => {
        const suggested = {};
        advanceAthletes.forEach(a => {
            if (a.shouldPromote) {
                const promo = resolveSuggestedPromotion(a, advanceTeam, advanceEscalaos, advanceSportTeams);
                if (promo) {
                    suggested[a.athleteProfileId] = promo;
                }
            }
        });
        setTeamPromotions(suggested);
    };

    const handleSubmitTeamAdvance = async () => {
        const promotedList = Object.entries(teamPromotions).map(([apId, info]) => ({
            athleteProfileId: parseInt(apId),
            destinationEscalaoId: info.destinationEscalaoId,
            destinationTeamId: info.destinationTeamId,
            destinationGender: info.destinationGender
        }));

        if (!window.confirm(`Tem a certeza que deseja avançar a época da equipa '${advanceTeam.name}' de ${advanceTeam.season} para ${advanceTeam.nextSeason}?`)) return;

        setAdvanceSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/${advanceTeam.id}/advance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ athletesToPromote: promotedList })
            });
            if (res.ok) {
                const result = await res.json();
                alert(result.message);
                setShowTeamAdvanceModal(false);
                await fetchTeams();
            } else {
                const err = await res.json();
                alert(err.message || 'Erro ao avançar época da equipa');
            }
        } catch (err) {
            console.error('Error submitting team advance:', err);
            alert('Erro de ligação ao submeter');
        } finally {
            setAdvanceSubmitting(false);
        }
    };

    const handleRevertTeamSeason = async (team) => {
        if (!window.confirm(`Reverter a época da equipa '${team.name}' (${team.season})?\n\nIsso irá eliminar esta equipa da época atual e restaurar a equipa da época anterior.`)) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/${team.id}/revert`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const result = await res.json();
                alert(result.message);
                await fetchTeams();
            } else {
                const err = await res.json();
                alert(err.message || 'Erro ao reverter época da equipa');
            }
        } catch (err) {
            console.error('Error reverting team season:', err);
            alert('Erro de ligação ao reverter');
        }
    };

    /* ── Season Advance (by sport) ─────────────────────────── */
    const openSeasonAdvanceModal = async (sportId) => {
        setAdvanceSportId(sportId);
        setAdvanceLoading(true);
        setPromotedAthletes({});
        setAdvancePromotionData({});
        setShowSeasonAdvanceModal(true);
        try {
            const token = localStorage.getItem('token');

            // Fetch both endpoints in parallel:
            // 1. Athletes per team (for the advance UI)
            // 2. Server-side promotion preview (same logic as PromotionManager)
            const [advanceRes, promoRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/teams/sport/${sportId}/athletes-for-advance`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE_URL}/api/escalaos/preview-promotion`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (!advanceRes.ok) {
                alert('Erro ao carregar dados da época');
                setShowSeasonAdvanceModal(false);
                return;
            }

            const advanceData = await advanceRes.json();
            const teamsData = advanceData.teams || [];
            const allTeams = advanceData.allTeams || [];
            setAdvanceTeamsData(teamsData);
            setAdvanceAllTeams(allTeams);

            // Build a set of teamIds that belong to this sport
            const sportTeamIds = new Set(teamsData.map(t => t.teamId));

            // Build promotion map from server: athleteId (= athleteProfileId) -> previewItem
            // Filter only athletes currently in teams of this sport
            const promoMap = {};
            if (promoRes.ok) {
                const promoData = await promoRes.json();
                promoData.forEach(item => {
                    // item.currentTeamId is the team the athlete is currently in
                    if (sportTeamIds.has(item.currentTeamId)) {
                        // item.athleteId === AthleteProfile.Id === athleteProfileId in the advance endpoint
                        promoMap[item.athleteId] = item;
                    }
                });
            }
            setAdvancePromotionData(promoMap);

            // Auto-pre-select athletes flagged for Promotion by the server
            // and use the server's targetTeamId as the suggested destination
            const autoPromoted = {};
            teamsData.forEach(teamData => {
                teamData.athletes.forEach(athlete => {
                    // athlete.athleteProfileId === item.athleteId in the promo map
                    const promoItem = promoMap[athlete.athleteProfileId];
                    if (promoItem && promoItem.movementType === 'Promotion') {
                        autoPromoted[athlete.athleteProfileId] = {
                            fromTeamId: teamData.teamId,
                            destinationTeamId: promoItem.targetTeamId || null,
                            autoSelected: true
                        };
                    }
                });
            });
            setPromotedAthletes(autoPromoted);


        } catch (err) {
            console.error('Error loading advance data:', err);
            alert('Erro de ligação');
            setShowSeasonAdvanceModal(false);
        } finally {
            setAdvanceLoading(false);
        }
    };

    const closeSeasonAdvanceModal = () => {
        setShowSeasonAdvanceModal(false);
        setAdvanceSportId(null);
        setAdvanceTeamsData([]);
        setAdvanceAllTeams([]);
        setPromotedAthletes({});
        setAdvancePromotionData({});
    };

    // Toggle promote checkbox for an athlete.
    // Uses server-side suggestion from advancePromotionData when available.
    const togglePromoteAthlete = (athlete, fromTeamId) => {
        setPromotedAthletes(prev => {
            if (prev[athlete.athleteProfileId]) {
                const next = { ...prev };
                delete next[athlete.athleteProfileId];
                return next;
            }
            // Use server-side suggested destination if available
            // promoMap is keyed by athleteId which equals athleteProfileId
            const promoItem = advancePromotionData[athlete.athleteProfileId];
            const suggested = promoItem?.targetTeamId || null;

            return {
                ...prev,
                [athlete.athleteProfileId]: { fromTeamId, destinationTeamId: suggested }
            };
        });
    };

    const setPromotionDestination = (athleteProfileId, destTeamId) => {
        setPromotedAthletes(prev => ({
            ...prev,
            [athleteProfileId]: { ...prev[athleteProfileId], destinationTeamId: destTeamId }
        }));
    };

    const handleDeselectAllAdvance = () => {
        setPromotedAthletes({});
    };

    const handleSelectSuggestedAdvance = () => {
        const autoPromoted = {};
        advanceTeamsData.forEach(teamData => {
            teamData.athletes.forEach(athlete => {
                const promoItem = advancePromotionData[athlete.athleteProfileId];
                if (promoItem && promoItem.movementType === 'Promotion') {
                    autoPromoted[athlete.athleteProfileId] = {
                        fromTeamId: teamData.teamId,
                        destinationTeamId: promoItem.targetTeamId || null,
                        autoSelected: true
                    };
                }
            });
        });
        setPromotedAthletes(autoPromoted);
    };

    const handleSubmitSeasonAdvance = async () => {
        const promotedList = Object.entries(promotedAthletes).map(([apId, info]) => ({
            athleteProfileId: parseInt(apId),
            fromTeamId: info.fromTeamId,
            destinationTeamId: info.destinationTeamId
        }));
        // Validate: all promoted athletes must have a destination
        if (promotedList.some(p => !p.destinationTeamId)) {
            alert('Por favor, selecione uma equipa de destino para todos os atletas marcados para subir.');
            return;
        }
        if (!window.confirm('Tem a certeza que deseja avançar a época? As equipas atuais serão desativadas e novas serão criadas para a próxima época.')) return;
        setAdvanceSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/advance-sport`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sportId: advanceSportId, athletesToPromote: promotedList })
            });
            if (res.ok) {
                const result = await res.json();
                alert(result.message);
                closeSeasonAdvanceModal();
                await fetchTeams();
            } else {
                const err = await res.json();
                alert(err.message || 'Erro ao avançar época');
            }
        } catch (err) {
            console.error('Error submitting season advance:', err);
            alert('Erro de ligação ao submeter');
        } finally {
            setAdvanceSubmitting(false);
        }
    };

    const handleRevertSeason = async (sportId, sportName, currentSeasonKey) => {
        if (!currentSeasonKey) {
            alert('Sem época ativa para reverter.');
            return;
        }
        const parts = currentSeasonKey.split('/');
        const prevSeason = parts.length === 2
            ? `${parseInt(parts[0]) - 1}/${parseInt(parts[1]) - 1}`
            : '?';
        if (!window.confirm(`Reverter "${sportName}" de volta para a época ${prevSeason}?\n\nIsso irá:\n• Desativar as equipas da época ${currentSeasonKey}\n• Reativar as equipas da época ${prevSeason}\n• Remover os atletas atribuídos durante o avanço`)) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/teams/revert-sport`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sportId })
            });
            if (res.ok) {
                const result = await res.json();
                alert(result.message);
                await fetchTeams();
            } else {
                const err = await res.json();
                alert(err.message || 'Erro ao reverter época');
            }
        } catch (err) {
            console.error('Error reverting season:', err);
            alert('Erro de ligação');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem a certeza que deseja eliminar esta equipa?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                await fetchTeams();
                alert('Equipa eliminada com sucesso!');
            } else {
                alert('Erro ao eliminar equipa');
            }
        } catch (error) {
            console.error('Error deleting team:', error);
            alert('Erro ao eliminar equipa');
        }
    };

    const openAddModal = (sportId = null) => {
        setEditingTeam(null);
        setFormData({
            ...initialFormState,
            sportId: sportId ? String(sportId) : ''
        });
        setShowModal(true);
    };

    const openEditModal = (team) => {
        setEditingTeam(team);
        setFormData({
            sportId: team.sportId,
            name: team.name,
            escalaoId: team.escalaoId || '',
            gender: team.gender,
            season: team.season || '',
            isActive: team.isActive,
            jerseyNumberMin: team.jerseyNumberMin ?? 0,
            jerseyNumberMax: team.jerseyNumberMax ?? 99,
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData(initialFormState);
        setEditingTeam(null);
        setShowModal(false);
    };

    const getGenderLabel = (gender) => {
        switch (gender) {
            case 1: return 'Masculino';
            case 2: return 'Feminino';
            case 3: return 'Misto';
            default: return 'Misto';
        }
    };

    const getGenderBadgeClass = (gender) => {
        switch (gender) {
            case 1: return 'gender-male';
            case 2: return 'gender-female';
            case 3: return 'gender-mixed';
            default: return 'gender-mixed';
        }
    };

    const filteredAthletes = useMemo(() => {
        const term = athleteSearch.toLowerCase().trim();
        return availableAthletes.filter(athlete => {
            const isInTeam = viewingTeam?.athletes?.some(a => a.userId === athlete.id);
            if (isInTeam) return false;
            if (!term) return true;
            return (
                athlete.fullName.toLowerCase().includes(term) ||
                (athlete.nif && athlete.nif.toLowerCase().includes(term)) ||
                getEmailBase(athlete.email).toLowerCase().includes(term)
            );
        });
    }, [availableAthletes, athleteSearch, viewingTeam]);

    const athletesAlreadyInTeam = useMemo(() => {
        if (!athleteSearch.trim()) return [];
        const term = athleteSearch.toLowerCase().trim();
        return availableAthletes.filter(athlete => {
            const isInTeam = viewingTeam?.athletes?.some(a => a.userId === athlete.id);
            if (!isInTeam) return false;
            return (
                athlete.fullName.toLowerCase().includes(term) ||
                (athlete.nif && athlete.nif.toLowerCase().includes(term)) ||
                getEmailBase(athlete.email).toLowerCase().includes(term)
            );
        });
    }, [availableAthletes, athleteSearch, viewingTeam]);

    const filteredCoaches = useMemo(() => {
        const term = coachSearch.toLowerCase().trim();
        return availableCoaches.filter(coach => {
            // coaches use coach.userId or coach.id ? The api endpoint returns users with coachProfile
            const isInTeam = viewingTeam?.coaches?.some(c => c.userId === coach.id);
            if (isInTeam) return false;
            if (!term) return true;
            return (
                coach.fullName.toLowerCase().includes(term) ||
                (coach.nif && coach.nif.toLowerCase().includes(term)) ||
                getEmailBase(coach.email).toLowerCase().includes(term)
            );
        });
    }, [availableCoaches, coachSearch, viewingTeam]);

    const coachesAlreadyInTeam = useMemo(() => {
        if (!coachSearch.trim()) return [];
        const term = coachSearch.toLowerCase().trim();
        return availableCoaches.filter(coach => {
            const isInTeam = viewingTeam?.coaches?.some(c => c.userId === coach.id);
            if (!isInTeam) return false;
            return (
                coach.fullName.toLowerCase().includes(term) ||
                (coach.nif && coach.nif.toLowerCase().includes(term)) ||
                getEmailBase(coach.email).toLowerCase().includes(term)
            );
        });
    }, [availableCoaches, coachSearch, viewingTeam]);

    const toggleAthleteSelection = (athleteProfileId) => {
        setSelectedAthletes(prev =>
            prev.includes(athleteProfileId)
                ? prev.filter(id => id !== athleteProfileId)
                : [...prev, athleteProfileId]
        );
    };

    const toggleCoachSelection = (coachId) => {
        setSelectedCoaches(prev =>
            prev.includes(coachId)
                ? prev.filter(id => id !== coachId)
                : [...prev, coachId]
        );
    };

    const handleSelectAllFiltered = () => {
        const filteredIds = filteredAthletes.map(a => a.athleteProfileId);
        setSelectedAthletes(prev => [...new Set([...prev, ...filteredIds])]);
    };

    const handleDeselectAllFiltered = () => {
        const filteredIds = filteredAthletes.map(a => a.athleteProfileId);
        setSelectedAthletes(prev => prev.filter(id => !filteredIds.includes(id)));
    };

    const areAllFilteredSelected = filteredAthletes.length > 0 && filteredAthletes.every(a => selectedAthletes.includes(a.athleteProfileId));

    const handleSelectAllFilteredCoaches = () => {
        const filteredIds = filteredCoaches.map(c => c.id);
        setSelectedCoaches(prev => [...new Set([...prev, ...filteredIds])]);
    };

    const handleDeselectAllFilteredCoaches = () => {
        const filteredIds = filteredCoaches.map(c => c.id);
        setSelectedCoaches(prev => prev.filter(id => !filteredIds.includes(id)));
    };

    const areAllFilteredCoachesSelected = filteredCoaches.length > 0 && filteredCoaches.every(c => selectedCoaches.includes(c.id));

    const handleUpdateJerseyNumber = async (teamId, athleteId, newJerseyNumber) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/athletes/${athleteId}/jersey`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ jerseyNumber: newJerseyNumber ? parseInt(newJerseyNumber) : null })
            });

            if (response.ok) {
                await fetchTeamDetails(teamId);
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao atualizar número');
            }
        } catch (error) {
            console.error('Error updating jersey:', error);
            alert('Erro ao atualizar número');
        }
    };

    const handleAddAthletes = async () => {
        if (selectedAthletes.length === 0) {
            alert('Por favor, selecione pelo menos um atleta.');
            return;
        }
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${viewingTeam.id}/athletes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ athleteProfileIds: selectedAthletes })
            });
            if (response.ok) {
                alert('Atletas adicionados com sucesso!');
                setSelectedAthletes([]);
                setAddingAthletes(false);
                await fetchTeamDetails(viewingTeam.id);
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao adicionar atletas');
            }
        } catch (error) {
            console.error('Error adding athletes:', error);
            alert('Erro ao adicionar atletas');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddCoaches = async () => {
        if (selectedCoaches.length === 0) {
            alert('Por favor, selecione pelo menos um treinador.');
            return;
        }
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${viewingTeam.id}/coaches`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ coachProfileIds: selectedCoaches })
            });
            if (response.ok) {
                alert('Treinadores adicionados com sucesso!');
                setSelectedCoaches([]);
                setAddingCoaches(false);
                await fetchTeamDetails(viewingTeam.id);
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao adicionar treinadores');
            }
        } catch (error) {
            console.error('Error adding coaches:', error);
            alert('Erro ao adicionar treinadores');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) return <FaSort className="sort-icon" />;
        if (sortConfig.direction === 'ascending') return <FaSortUp className="sort-icon active" />;
        return <FaSortDown className="sort-icon active" />;
    };

    const toggleSportHistory = (sportId) => {
        setExpandedSportHistory(prev => ({ ...prev, [sportId]: !prev[sportId] }));
    };

    const toggleCurrentSeason = (sportId) => {
        setExpandedCurrentSeasons(prev => ({
            ...prev,
            [sportId]: prev[sportId] === false ? true : false
        }));
    };

    const toggleHistorySeason = (sportId, season) => {
        const key = `${sportId}__${season}`;
        setExpandedHistorySeasons(prev => ({ ...prev, [key]: !prev[key] }));
    };

    /* ── teamsBySport: main data structure ── */
    const teamsBySport = useMemo(() => {
        let filtered = [...teams];
        if (isCoordinator && coordinatorSportId) {
            filtered = filtered.filter(t => t.sportId === parseInt(coordinatorSportId));
        } else if (selectedSportFilter) {
            filtered = filtered.filter(t => t.sportId === parseInt(selectedSportFilter));
        }

        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = sortConfig.key === 'gender' ? getGenderLabel(a.gender) : a[sortConfig.key];
                let bVal = sortConfig.key === 'gender' ? getGenderLabel(b.gender) : b[sortConfig.key];
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        const sportMap = {};
        filtered.forEach(team => {
            if (!sportMap[team.sportId]) {
                sportMap[team.sportId] = {
                    sport: { id: team.sportId, name: team.sportName },
                    seasonMap: {}
                };
            }
            const season = team.season || 'Sem Época';
            if (!sportMap[team.sportId].seasonMap[season]) {
                sportMap[team.sportId].seasonMap[season] = [];
            }
            sportMap[team.sportId].seasonMap[season].push(team);
        });

        return Object.values(sportMap).map(entry => {
            const activeMap = {};
            const historyMap = {};

            Object.keys(entry.seasonMap).forEach(season => {
                const activeInSeason = entry.seasonMap[season].filter(t => t.isActive);
                if (activeInSeason.length > 0) {
                    activeMap[season] = activeInSeason;
                }
                const inactiveInSeason = entry.seasonMap[season].filter(t => !t.isActive);
                if (inactiveInSeason.length > 0) {
                    historyMap[season] = inactiveInSeason;
                }
            });

            const activeSeasons = Object.keys(activeMap)
                .sort((a, b) => b.localeCompare(a))
                .map(s => ({ season: s, teams: activeMap[s] }));

            const historySeasons = Object.keys(historyMap)
                .sort((a, b) => b.localeCompare(a))
                .map(s => ({ season: s, teams: historyMap[s] }));

            return {
                sport: entry.sport,
                activeSeasons,
                historySeasons,
                totalTeams: Object.values(entry.seasonMap).flat().length
            };
        }).sort((a, b) => a.sport.name.localeCompare(b.sport.name, 'pt'));
    }, [teams, selectedSportFilter, sortConfig]);

    const seasonOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear - 5; i <= currentYear + 10; i++) {
            years.push(`${i}/${i + 1}`);
        }
        return years;
    }, []);

    const hasActiveFilters = athleteSearch.trim() !== '';

    const sportsWithTeams = useMemo(() =>
        sports.filter(s => teams.some(t => t.sportId === s.id)),
        [sports, teams]
    );

    if (loading) {
        return (
            <div className="teams-manager">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>A carregar equipas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="teams-manager">

            {/* ── Header ── */}
            <div className="teams-manager-header">
                <div className="header-content">
                    <h1>Gestão de Equipas</h1>
                    <p className="header-subtitle">
                        {teams.length} {teams.length === 1 ? 'equipa' : 'equipas'} em {sportsWithTeams.length} {sportsWithTeams.length === 1 ? 'modalidade' : 'modalidades'}
                    </p>
                </div>
                <button className="teams-btn-add" onClick={() => openAddModal()}>
                    <FaPlus /> Nova Equipa
                </button>
            </div>

            {/* ── Sport Pills Navigation ── */}
            {teams.length > 0 && (
                <div className="sport-pills-nav">
                    <button
                        className={`sport-pill ${!selectedSportFilter ? 'active' : ''}`}
                        onClick={() => setSelectedSportFilter('')}
                    >
                        Todas
                        <span className="sport-pill-count">{teams.length}</span>
                    </button>
                    {sportsWithTeams.map(sport => {
                        const count = teams.filter(t => t.sportId === sport.id).length;
                        return (
                            <button
                                key={sport.id}
                                className={`sport-pill ${parseInt(selectedSportFilter) === sport.id ? 'active' : ''}`}
                                onClick={() => setSelectedSportFilter(parseInt(selectedSportFilter) === sport.id ? '' : sport.id)}
                            >
                                {sport.name}
                                <span className="sport-pill-count">{count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── Empty State ── */}
            {teams.length === 0 ? (
                <div className="teams-empty-state">
                    <div className="teams-empty-icon">🏆</div>
                    <h3>Nenhuma equipa registada</h3>
                    <p>Comece por criar a primeira equipa</p>
                    <button className="teams-btn-add" onClick={() => openAddModal()}>
                        <FaPlus /> Criar Primeira Equipa
                    </button>
                </div>
            ) : teamsBySport.length === 0 ? (
                <div className="teams-empty-state">
                    <div className="teams-empty-icon">🔍</div>
                    <h3>Sem equipas para esta modalidade</h3>
                    <p>Experimente selecionar outra modalidade</p>
                </div>
            ) : (
                /* ── Sport Sections ── */
                <div className="sports-sections-wrapper">
                    {teamsBySport.map(({ sport, activeSeasons, historySeasons, totalTeams }) => (
                        <div className="sport-section" key={sport.id}>

                            {/* Sport Header */}
                            <div className="sport-section-header">
                                <div className="sport-section-title">
                                    <h2>{sport.name}</h2>
                                    <span className="sport-total-badge">
                                        {totalTeams} {totalTeams === 1 ? 'equipa' : 'equipas'}
                                    </span>
                                </div>
                                <div className="sport-header-actions">
                                    {historySeasons.length > 0 && (
                                        <button
                                            className="sport-revert-btn"
                                            onClick={() => handleRevertSeason(sport.id, sport.name, activeSeasons[0]?.season)}
                                            title="Reverter para a época anterior"
                                        >
                                            <FaUndo /> Reverter
                                        </button>
                                    )}
                                    <button className="sport-add-btn" onClick={() => openAddModal(sport.id)}>
                                        <FaPlus /> Nova Equipa
                                    </button>
                                </div>
                            </div>

                            <div className="sport-section-body">

                                {/* Current Active Seasons */}
                                {activeSeasons.length > 0 && activeSeasons.map(({ season, teams: currentTeams }) => {
                                    const key = `${sport.id}__${season}`;
                                    const isOpen = expandedCurrentSeasons[key] !== false;
                                    return (
                                        <div key={season} className={`current-season-area ${isOpen ? 'open' : ''}`}>
                                            <div
                                                className="season-bar current clickable-season-header"
                                                onClick={() => toggleCurrentSeason(key)}
                                            >
                                                <FaChevronDown className={`season-toggle-chevron ${isOpen ? 'rotated' : ''}`} />
                                                <span className="season-bar-name">{season}</span>
                                                <span className="season-bar-count">{currentTeams.length} {currentTeams.length === 1 ? 'equipa' : 'equipas'}</span>
                                            </div>
                                            {isOpen && (
                                                <div className="teams-card-grid">
                                                    {currentTeams.map(team => (
                                                        <TeamCard
                                                            key={team.id}
                                                            team={team}
                                                            onView={fetchTeamDetails}
                                                            onEdit={openEditModal}
                                                            onDelete={handleDelete}
                                                            onAdvance={openTeamAdvanceModal}
                                                            onRevert={handleRevertTeamSeason}
                                                            onAttendance={handleOpenAttendance}
                                                            getGenderBadgeClass={getGenderBadgeClass}
                                                            getGenderLabel={getGenderLabel}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* No current season teams */}
                                {activeSeasons.length === 0 && historySeasons.length > 0 && (
                                    <div className="no-current-season">
                                        <span>Sem equipas na época atual</span>
                                    </div>
                                )}

                                {/* History */}
                                {historySeasons.length > 0 && (
                                    <div className="sport-history-container">
                                        <button
                                            className={`sport-history-toggle ${expandedSportHistory[sport.id] ? 'open' : ''}`}
                                            onClick={() => toggleSportHistory(sport.id)}
                                        >
                                            <FaHistory className="history-toggle-icon" />
                                            <span>Histórico</span>
                                            <span className="history-toggle-meta">
                                                {historySeasons.reduce((sum, s) => sum + s.teams.length, 0)} {historySeasons.reduce((sum, s) => sum + s.teams.length, 0) === 1 ? 'equipa' : 'equipas'} · {historySeasons.length} {historySeasons.length === 1 ? 'época' : 'épocas'}
                                            </span>
                                            <FaChevronDown className={`history-toggle-chevron ${expandedSportHistory[sport.id] ? 'rotated' : ''}`} />
                                        </button>

                                        {expandedSportHistory[sport.id] && (
                                            <div className="sport-history-content">
                                                {historySeasons.map(({ season, teams: seasonTeams }) => {
                                                    const key = `${sport.id}__${season}`;
                                                    const isOpen = expandedHistorySeasons[key];
                                                    return (
                                                        <div key={season} className={`history-season-item ${isOpen ? 'open' : ''}`}>
                                                            <div
                                                                className="history-season-header"
                                                                onClick={() => toggleHistorySeason(sport.id, season)}
                                                            >
                                                                <FaChevronDown className={`history-season-chevron ${isOpen ? 'rotated' : ''}`} />
                                                                <span className="history-season-year">{season}</span>
                                                                <span className="history-season-count">
                                                                    {seasonTeams.length} {seasonTeams.length === 1 ? 'equipa' : 'equipas'}
                                                                </span>
                                                            </div>
                                                            {isOpen && (
                                                                <div className="history-season-table-wrapper">
                                                                    <TeamsTable
                                                                        teams={seasonTeams}
                                                                        onView={fetchTeamDetails}
                                                                        onEdit={openEditModal}
                                                                        onDelete={handleDelete}
                                                                        onRevert={handleRevertTeamSeason}
                                                                        onAttendance={handleOpenAttendance}
                                                                        getSortIcon={getSortIcon}
                                                                        handleSort={handleSort}
                                                                        getGenderBadgeClass={getGenderBadgeClass}
                                                                        getGenderLabel={getGenderLabel}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Single Team Season Advance Modal ── */}
            {showTeamAdvanceModal && advanceTeam && createPortal(
                <div className="teams-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowTeamAdvanceModal(false); }}>
                    <div className="teams-modal season-advance-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px' }}>
                        <div className="teams-modal-header">
                            <div className="season-advance-title">
                                <FaForward className="season-advance-icon" />
                                <div>
                                    <h2>Avançar Época — {advanceTeam.name}</h2>
                                    <p className="season-advance-subtitle">
                                        Época Atual: <strong>{advanceTeam.season}</strong> &nbsp;→&nbsp; Nova Época: <strong>{advanceTeam.nextSeason}</strong>
                                    </p>
                                </div>
                            </div>
                            <button className="teams-modal-close" onClick={() => setShowTeamAdvanceModal(false)}>×</button>
                        </div>

                        <div className="season-advance-body">
                            {advanceLoading ? (
                                <div className="season-advance-loading">
                                    <div className="spinner" />
                                    <p>A carregar atletas da equipa...</p>
                                </div>
                            ) : advanceAthletes.length === 0 ? (
                                <div className="season-advance-empty">
                                    <span>Esta equipa não tem atletas associados.</span>
                                </div>
                            ) : (
                                <>
                                    <div className="season-advance-toolbar">
                                        <div className="season-advance-toolbar-info">
                                            <strong>{Object.keys(teamPromotions).length}</strong> de <strong>{advanceAthletes.length}</strong> atletas marcados para subir de escalão
                                        </div>
                                        <div className="season-advance-toolbar-buttons">
                                            <button
                                                type="button"
                                                className="season-advance-tool-btn danger"
                                                onClick={() => setTeamPromotions({})}
                                                disabled={Object.keys(teamPromotions).length === 0}
                                                title="Manter todos os atletas nesta equipa para a próxima época"
                                            >
                                                <FaTimes style={{ fontSize: '0.75rem' }} /> Desselecionar todos
                                            </button>
                                            <button
                                                type="button"
                                                className="season-advance-tool-btn secondary"
                                                onClick={handleSelectSuggestedSingleAdvance}
                                                title="Selecionar atletas que ultrapassam a idade máxima do escalão"
                                            >
                                                <FaUndo style={{ fontSize: '0.75rem' }} /> Sugerir Promoções por Idade
                                            </button>
                                        </div>
                                    </div>

                                    <div className="advance-team-block">
                                        <table className="advance-athletes-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '130px' }}>Sobe de Escalão</th>
                                                    <th>Atleta</th>
                                                    <th>Idade Estimada</th>
                                                    <th>Nº Dorsal</th>
                                                    <th>Novo Escalão / Destino</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {advanceAthletes.map(athlete => {
                                                    const isPromoted = !!teamPromotions[athlete.athleteProfileId];
                                                    const promoInfo = teamPromotions[athlete.athleteProfileId];

                                                    return (
                                                        <tr
                                                            key={athlete.athleteProfileId}
                                                            className={[
                                                                isPromoted ? 'row-promoted' : '',
                                                                athlete.shouldPromote && !isPromoted ? 'row-ages-out' : ''
                                                            ].filter(Boolean).join(' ')}
                                                        >
                                                            <td className="advance-checkbox-cell">
                                                                <label className="advance-toggle-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isPromoted}
                                                                        onChange={() => togglePromoteSingleAthlete(athlete)}
                                                                    />
                                                                    <span className="advance-toggle-custom" />
                                                                </label>
                                                                {athlete.shouldPromote && !isPromoted && (
                                                                    <span
                                                                        className="advance-ages-out-badge"
                                                                        title={`${athlete.name} (${athlete.age} anos) ultrapassa o limite de idade do escalão atual`}
                                                                    >
                                                                        ⚠
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="advance-athlete-name">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                    <span>{athlete.name}</span>
                                                                    {(() => {
                                                                        const effGender = getEffectiveAthleteGender(athlete, advanceTeam);
                                                                        return (
                                                                            <span className={`gender-badge ${effGender === 1 ? 'gender-male' : effGender === 2 ? 'gender-female' : 'gender-mixed'}`} style={{ fontSize: '0.7rem', padding: '1px 6px' }}>
                                                                                {effGender === 1 ? 'Masculino' : effGender === 2 ? 'Feminino' : 'Misto'}
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </td>
                                                            <td className="advance-age-cell">
                                                                {athlete.age != null ? (
                                                                    <span className={`advance-age-badge ${athlete.shouldPromote ? 'age-out' : ''}`}>
                                                                        {athlete.age} anos
                                                                    </span>
                                                                ) : '—'}
                                                            </td>
                                                            <td className="advance-jersey">
                                                                {athlete.jerseyNumber ?? '—'}
                                                            </td>
                                                            <td className="advance-dest-cell">
                                                                {isPromoted ? (
                                                                    <select
                                                                        className="advance-dest-select"
                                                                        value={getSelectValue(promoInfo, athlete, uniqueSportTeams, advanceTeam)}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            const defaultGen = getEffectiveAthleteGender(athlete, advanceTeam);
                                                                            if (!val) {
                                                                                setSingleAthleteDestination(athlete.athleteProfileId, null, null, null);
                                                                            } else if (val.startsWith('team_')) {
                                                                                const teamId = parseInt(val.replace('team_', ''));
                                                                                const targetTeam = advanceSportTeams.find(t => t.id === teamId);
                                                                                setSingleAthleteDestination(
                                                                                    athlete.athleteProfileId,
                                                                                    targetTeam ? targetTeam.escalaoId : null,
                                                                                    teamId,
                                                                                    targetTeam ? targetTeam.gender : defaultGen
                                                                                );
                                                                            } else if (val.includes('_gender_')) {
                                                                                const parts = val.replace('escalao_', '').split('_gender_');
                                                                                const escalaoId = parseInt(parts[0]);
                                                                                const gender = parseInt(parts[1]);
                                                                                setSingleAthleteDestination(athlete.athleteProfileId, escalaoId, null, gender);
                                                                            } else if (val.startsWith('escalao_')) {
                                                                                const escalaoId = parseInt(val.replace('escalao_', ''));
                                                                                setSingleAthleteDestination(athlete.athleteProfileId, escalaoId, null, defaultGen);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <option value="">— Selecione destino —</option>
                                                                        {advanceEscalaos.map(esc => {
                                                                            const teamsInEscalao = uniqueSportTeams.filter(t => t.escalaoId === esc.id);
                                                                            const ageLabel = esc.minAge ? ` (${esc.minAge}–${esc.maxAge || '∞'} anos)` : '';

                                                                            if (teamsInEscalao.length === 0) {
                                                                                return (
                                                                                    <option key={`escalao_${esc.id}`} value={`escalao_${esc.id}`}>
                                                                                        Escalão: {esc.name}{ageLabel} (Criar equipa automaticamente)
                                                                                    </option>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <optgroup key={esc.id} label={`${esc.name}${ageLabel}`}>
                                                                                    {teamsInEscalao.map(t => {
                                                                                        const nameLower = t.name.toLowerCase();
                                                                                        const nameHasGender = nameLower.includes('masculin') || nameLower.includes('feminin') || nameLower.includes('misto') || nameLower.includes('mista');
                                                                                        const genderLabel = t.gender === 1 ? 'Masculino' : t.gender === 2 ? 'Feminino' : 'Misto';
                                                                                        const displayName = nameHasGender ? t.name : `${t.name} (${genderLabel})`;
                                                                                        return (
                                                                                            <option key={`team_${t.id}`} value={`team_${t.id}`}>
                                                                                                Equipa: {displayName}
                                                                                            </option>
                                                                                        );
                                                                                    })}
                                                                                    <option value={`escalao_${esc.id}`}>
                                                                                        Criar nova equipa para {esc.name}
                                                                                    </option>
                                                                                </optgroup>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                ) : (
                                                                    <span className="advance-stay-label">Fica nesta equipa</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="season-advance-footer">
                            <div className="season-advance-summary">
                                <span className="summary-stat">
                                    <strong>{Object.keys(teamPromotions).length}</strong> a subir de escalão
                                </span>
                                <span className="summary-stat">
                                    <strong>{advanceAthletes.length - Object.keys(teamPromotions).length}</strong> ficam na equipa ({advanceTeam.name})
                                </span>
                            </div>
                            <div className="season-advance-actions">
                                <button className="teams-btn-cancel" onClick={() => setShowTeamAdvanceModal(false)} disabled={advanceSubmitting}>
                                    Cancelar
                                </button>
                                <button
                                    className="teams-btn-advance-submit"
                                    onClick={handleSubmitTeamAdvance}
                                    disabled={advanceSubmitting || advanceLoading}
                                >
                                    {advanceSubmitting ? <><div className="spinner-small" /> A processar...</> : <><FaForward /> Confirmar Avanço de Época</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Season Advance Modal ── */}
            {showSeasonAdvanceModal && (
                <div className="teams-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeSeasonAdvanceModal(); }}>
                    <div className="teams-modal season-advance-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="teams-modal-header">
                            <div className="season-advance-title">
                                <FaForward className="season-advance-icon" />
                                <div>
                                    <h2>Avançar Época</h2>
                                    <p className="season-advance-subtitle">
                                        {advanceTeamsData[0]?.season
                                            ? `${advanceTeamsData[0].season} → ${advanceTeamsData[0].season.replace(/(\d+)\/(\d+)/, (_, a, b) => `${+a + 1}/${+b + 1}`)}`
                                            : 'Nova Época'}
                                    </p>
                                </div>
                            </div>
                            <button className="teams-modal-close" onClick={closeSeasonAdvanceModal}>×</button>
                        </div>

                        <div className="season-advance-body">
                            {advanceLoading ? (
                                <div className="season-advance-loading">
                                    <div className="spinner" />
                                    <p>A carregar atletas...</p>
                                </div>
                            ) : advanceTeamsData.length === 0 ? (
                                <div className="season-advance-empty">
                                    <span>Sem equipas ativas nesta modalidade.</span>
                                </div>
                            ) : (
                                <>
                                    <div className="season-advance-toolbar">
                                        <div className="season-advance-toolbar-info">
                                            <strong>{Object.keys(promotedAthletes).length}</strong> atleta(s) selecionado(s) para subir de escalão
                                        </div>
                                        <div className="season-advance-toolbar-buttons">
                                            <button
                                                type="button"
                                                className="season-advance-tool-btn danger"
                                                onClick={handleDeselectAllAdvance}
                                                disabled={Object.keys(promotedAthletes).length === 0}
                                                title="Deselecionar todos os atletas"
                                            >
                                                <FaTimes style={{ fontSize: '0.75rem' }} /> Deselecionar todos
                                            </button>
                                            <button
                                                type="button"
                                                className="season-advance-tool-btn secondary"
                                                onClick={handleSelectSuggestedAdvance}
                                                title="Restaurar seleção automática recomendada pelo sistema"
                                            >
                                                <FaUndo style={{ fontSize: '0.75rem' }} /> Selecionar sugeridos
                                            </button>
                                        </div>
                                    </div>

                                    {advanceTeamsData.map(teamData => (
                                        <div key={teamData.teamId} className="advance-team-block">
                                            <div className="advance-team-header">
                                                <span className="advance-team-name">{teamData.teamName}</span>
                                                {teamData.escalaoName && <span className="advance-team-escalao">{teamData.escalaoName}</span>}
                                                <span className={`gender-badge ${teamData.gender === 1 ? 'gender-male' : teamData.gender === 2 ? 'gender-female' : 'gender-mixed'}`}>
                                                    {teamData.gender === 1 ? 'Masculino' : teamData.gender === 2 ? 'Feminino' : 'Misto'}
                                                </span>
                                                <span className="advance-team-count">{teamData.athletes.length} atletas</span>
                                            </div>

                                            {teamData.athletes.length === 0 ? (
                                                <p className="advance-team-empty">Sem atletas nesta equipa.</p>
                                            ) : (
                                                <table className="advance-athletes-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Sobe de Escalão</th>
                                                            <th>Atleta</th>
                                                            <th>Idade</th>
                                                            <th>Nº</th>
                                                            <th>Novo Escalão</th>
                                                            <th>Equipa Destino</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {teamData.athletes.map(athlete => {
                                                            const isPromoted = !!promotedAthletes[athlete.athleteProfileId];
                                                            const promoInfo = promotedAthletes[athlete.athleteProfileId];
                                                            // Server-side promotion info for this athlete
                                                            // athleteProfileId == athleteId from the preview-promotion endpoint
                                                            const promoItem = advancePromotionData[athlete.athleteProfileId];
                                                            // Athlete flagged for promotion by server
                                                            const serverFlagged = promoItem?.movementType === 'Promotion';
                                                            // Available destination teams: exclude the current team
                                                            const destOptions = advanceAllTeams.filter(t => t.id !== teamData.teamId);
                                                            // Calculate age for all athletes (from promoItem if suggested, or directly from birthDate)
                                                            const calcCurrentAge = (birthDate) => {
                                                                if (!birthDate) return null;
                                                                const today = new Date();
                                                                const birth = new Date(birthDate);
                                                                let age = today.getFullYear() - birth.getFullYear();
                                                                const m = today.getMonth() - birth.getMonth();
                                                                if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                                                                return age;
                                                            };
                                                            const athleteAge = promoItem?.age ?? calcCurrentAge(athlete.birthDate);
                                                            const hasSuggestion = isPromoted && promoInfo?.destinationTeamId;

                                                            return (
                                                                <tr
                                                                    key={athlete.athleteProfileId}
                                                                    className={[
                                                                        isPromoted ? 'row-promoted' : '',
                                                                        serverFlagged && !isPromoted ? 'row-ages-out' : ''
                                                                    ].filter(Boolean).join(' ')}
                                                                >
                                                                    <td className="advance-checkbox-cell">
                                                                        <label
                                                                            className="advance-toggle-label"
                                                                            title={serverFlagged && !isPromoted
                                                                                ? `${athlete.name} deve subir de escalão (${athleteAge} anos — escalão atual já não é válido)`
                                                                                : ''}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isPromoted}
                                                                                onChange={() => togglePromoteAthlete(athlete, teamData.teamId)}
                                                                            />
                                                                            <span className="advance-toggle-custom" />
                                                                        </label>
                                                                        {serverFlagged && !isPromoted && (
                                                                            <span
                                                                                className="advance-ages-out-badge"
                                                                                title={`${athlete.name} tem ${athleteAge} anos — deve subir para ${promoItem.newEscalaoName}`}
                                                                            >
                                                                                ⚠
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="advance-athlete-name">
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                            <span>{athlete.name}</span>
                                                                            {(() => {
                                                                                const effGender = getEffectiveAthleteGender(athlete, teamData);
                                                                                return (
                                                                                    <span className={`gender-badge ${effGender === 1 ? 'gender-male' : effGender === 2 ? 'gender-female' : 'gender-mixed'}`} style={{ fontSize: '0.7rem', padding: '1px 6px' }}>
                                                                                        {effGender === 1 ? 'Masculino' : effGender === 2 ? 'Feminino' : 'Misto'}
                                                                                    </span>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    </td>
                                                                    <td className="advance-age-cell">
                                                                        {athleteAge != null ? (
                                                                            <span
                                                                                className={['advance-age-badge', serverFlagged ? 'age-out' : ''].filter(Boolean).join(' ')}
                                                                                title={serverFlagged ? `Fora do intervalo do escalão atual` : ''}
                                                                            >
                                                                                {athleteAge} anos
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td className="advance-jersey">
                                                                        {athlete.jerseyNumber ?? '—'}
                                                                    </td>
                                                                    <td className="advance-new-escalao-cell">
                                                                        {promoItem && serverFlagged ? (
                                                                            <span className="advance-new-escalao-badge">
                                                                                {promoItem.newEscalaoName}
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td className="advance-dest-cell">
                                                                        {isPromoted ? (
                                                                            <>
                                                                                <select
                                                                                    className={`advance-dest-select ${!hasSuggestion ? 'no-suggestion' : ''}`}
                                                                                    value={promoInfo.destinationTeamId ?? ''}
                                                                                    onChange={(e) => setPromotionDestination(
                                                                                        athlete.athleteProfileId,
                                                                                        e.target.value ? parseInt(e.target.value) : null
                                                                                    )}
                                                                                >
                                                                                    <option value="">— Selecionar equipa —</option>
                                                                                    {destOptions.map(t => {
                                                                                        const nameLower = (t.name || '').toLowerCase();
                                                                                        const nameHasGender = nameLower.includes('masculin') || nameLower.includes('feminin') || nameLower.includes('misto') || nameLower.includes('mista');
                                                                                        const genderLabel = t.gender === 1 ? 'Masculino' : t.gender === 2 ? 'Feminino' : 'Misto';
                                                                                        const teamDisplayName = nameHasGender ? t.name : `${t.name} (${genderLabel})`;
                                                                                        return (
                                                                                            <option key={t.id} value={t.id}>
                                                                                                {teamDisplayName}{t.escalaoName ? ` (${t.escalaoName}` : ''}
                                                                                                {(t.escalaoMinAge != null || t.escalaoMaxAge != null)
                                                                                                    ? `, ${t.escalaoMinAge ?? 0}–${t.escalaoMaxAge ?? '∞'} anos)`
                                                                                                    : t.escalaoName ? ')' : ''
                                                                                                }
                                                                                            </option>
                                                                                        );
                                                                                    })}
                                                                                </select>
                                                                                {!hasSuggestion && (
                                                                                    <span className="advance-no-team-hint">Sem equipa compatível — selecione manualmente</span>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <span className="advance-stay-label">Fica na equipa</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>

                                    ))}
                                </>
                            )}
                        </div>

                        <div className="season-advance-footer">
                            <div className="season-advance-summary">
                                <span className="summary-stat">
                                    <strong>{Object.keys(promotedAthletes).length}</strong> a subir de escalão
                                </span>
                                <span className="summary-stat">
                                    <strong>{advanceTeamsData.reduce((s, t) => s + t.athletes.length, 0) - Object.keys(promotedAthletes).length}</strong> ficam nas mesmas equipas
                                </span>
                            </div>
                            <div className="season-advance-actions">
                                <button className="teams-btn-cancel" onClick={closeSeasonAdvanceModal} disabled={advanceSubmitting}>
                                    Cancelar
                                </button>
                                <button
                                    className="teams-btn-advance-submit"
                                    onClick={handleSubmitSeasonAdvance}
                                    disabled={advanceSubmitting || advanceLoading}
                                >
                                    {advanceSubmitting ? <><div className="spinner-small" /> A processar...</> : <><FaForward /> Submeter Avanço de Época</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Create/Edit Team Modal ── */}
            {showModal && createPortal(
                <div className="teams-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm(); } }}>
                    <div className="teams-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="teams-modal-header">
                            <h2>{editingTeam ? 'Editar Equipa' : 'Nova Equipa'}</h2>
                            <button className="teams-modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="teams-modal-form">
                            <div className="teams-form-row">
                                <div className="teams-form-section">
                                    <label className="teams-form-label">Modalidade *</label>
                                    <select
                                        className="teams-form-select"
                                        value={formData.sportId}
                                        onChange={(e) => updateFormField('sportId', e.target.value)}
                                        required
                                    >
                                        <option value="">Selecione a modalidade</option>
                                        {sports.map(sport => (
                                            <option key={sport.id} value={sport.id}>{sport.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="teams-form-section">
                                    <label className="teams-form-label">Escalão</label>
                                    <EscalaoSearchSelect
                                        escalaos={escalaos}
                                        value={formData.escalaoId}
                                        onChange={(val) => updateFormField('escalaoId', val)}
                                    />
                                </div>

                                <div className="teams-form-section">
                                    <label className="teams-form-label">Género</label>
                                    <select
                                        className="teams-form-select"
                                        value={formData.gender}
                                        onChange={(e) => updateFormField('gender', e.target.value)}
                                    >
                                        <option value="1">Masculino</option>
                                        <option value="2">Feminino</option>
                                        <option value="3">Misto</option>
                                    </select>
                                </div>
                            </div>

                            <div className="teams-form-section">
                                <label className="teams-form-label">Nome da Equipa *</label>
                                <input
                                    type="text"
                                    className="teams-form-input"
                                    placeholder="Nome gerado automaticamente"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                                    O nome é gerado automaticamente mas pode ser editado
                                </small>
                            </div>

                            <div className="teams-form-section">
                                <label className="teams-form-label">Época</label>
                                <select
                                    className="teams-form-select scrollable-dropdown"
                                    value={formData.season}
                                    onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                                >
                                    <option value="">Selecione a época</option>
                                    {seasonOptions.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="teams-form-section">
                                <label className="teams-form-label">Intervalo de Números</label>
                                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        className="teams-form-input"
                                        placeholder="Mínimo (ex. 0)"
                                        min="0"
                                        max="999"
                                        value={formData.jerseyNumberMin}
                                        onChange={(e) => setFormData({ ...formData, jerseyNumberMin: e.target.value })}
                                        style={{ flex: 1 }}
                                    />
                                    <span style={{ color: '#666', fontWeight: 600 }}>–</span>
                                    <input
                                        type="number"
                                        className="teams-form-input"
                                        placeholder="Máximo (ex. 99)"
                                        min="0"
                                        max="999"
                                        value={formData.jerseyNumberMax}
                                        onChange={(e) => setFormData({ ...formData, jerseyNumberMax: e.target.value })}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                                    Deixe em branco para não restringir os números de dorsal
                                </small>
                            </div>

                            <div className="form-section">
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                    <span className="toggle-slider"></span>
                                    <span className="toggle-label">Equipa Ativa</span>
                                </label>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="teams-btn-submit" disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                                            A guardar...
                                        </>
                                    ) : (
                                        editingTeam ? 'Atualizar Equipa' : 'Criar Equipa'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Team Details Modal ── */}
            {showDetailsModal && viewingTeam && createPortal(
                <div className="teams-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowDetailsModal(false); setViewingTeam(null); setAddingAthletes(false); } }}>
                    <div className="teams-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <div>
                                <h2>{viewingTeam.name}</h2>
                                <p style={{ margin: 0, color: '#666' }}>
                                    {viewingTeam.sportName} • {viewingTeam.category} • {viewingTeam.season}
                                </p>
                            </div>
                            <button
                                className="modal-close"
                                onClick={() => { setShowDetailsModal(false); setViewingTeam(null); setAddingAthletes(false); }}
                            >
                                ×
                            </button>
                        </div>

                        <div className="teams-modal-form">
                            <div key={viewingTeam.id} className="roster-entrance-wrapper">
                                {/* Coaches Section */}
                                <div className="roster-section">
                                    <h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between', width: '100%' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <FaUsers style={{ color: '#003380' }} />
                                                <span>Treinadores</span>
                                                <span style={{
                                                    background: '#e3f2fd',
                                                    color: '#1976d2',
                                                    borderRadius: '20px',
                                                    padding: '2px 10px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600
                                                }}>
                                                    {viewingTeam.coaches?.length || 0}
                                                </span>
                                            </div>
                                            {!addingCoaches && (
                                                <button
                                                    className="teams-btn-submit"
                                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                                    onClick={() => { setAddingCoaches(true); fetchAvailableCoaches(); }}
                                                >
                                                    <FaUserPlus /> Adicionar Treinadores
                                                </button>
                                            )}
                                            {addingCoaches && (
                                                <button
                                                    className="btn-cancel"
                                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                                    onClick={() => { setAddingCoaches(false); setSelectedCoaches([]); setCoachSearch(''); }}
                                                >
                                                    <FaTimes /> Cancelar
                                                </button>
                                            )}
                                        </div>
                                    </h3>

                                    {addingCoaches ? (
                                        <div className="add-athlete-layout">
                                            {/* Left: search + coach list */}
                                            <div className="add-athlete-search-panel">
                                                <div className="athlete-search-bar-wrap">
                                                    <FaSearch className="athlete-search-icon" />
                                                    <input
                                                        type="text"
                                                        className="athlete-search-input"
                                                        placeholder="Pesquisar por nome, NIF ou email..."
                                                        value={coachSearch}
                                                        onChange={(e) => setCoachSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                    {coachSearch && (
                                                        <button className="athlete-search-clear" onClick={() => setCoachSearch('')} title="Limpar">
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                </div>

                                                {filteredCoaches.length > 0 && (
                                                    <div className="athlete-list-controls">
                                                        <button
                                                            className="athlete-list-ctrl-btn"
                                                            onClick={areAllFilteredCoachesSelected ? handleDeselectAllFilteredCoaches : handleSelectAllFilteredCoaches}
                                                        >
                                                            {areAllFilteredCoachesSelected ? <><FaUserCheck /> Desselecionar todos</> : <><FaUserPlus /> Selecionar todos</>}
                                                        </button>
                                                        <span className="athlete-list-count">{filteredCoaches.length} disponíveis</span>
                                                    </div>
                                                )}

                                                {coachesLoading ? (
                                                    <div className="athletes-loading">
                                                        <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }}></div>
                                                        <span>A carregar treinadores...</span>
                                                    </div>
                                                ) : (
                                                    <div className="athletes-list">
                                                        {filteredCoaches.length === 0 && coachesAlreadyInTeam.length === 0 && (
                                                            <div className="no-results">
                                                                <span className="no-results-icon">🔍</span>
                                                                <p>{coachSearch ? 'Sem resultados para a pesquisa' : 'Sem treinadores disponíveis'}</p>
                                                            </div>
                                                        )}

                                                        {filteredCoaches.map(coach => {
                                                            const isSelected = selectedCoaches.includes(coach.id);
                                                            return (
                                                                <div
                                                                    key={coach.id}
                                                                    className={`athlete-item ${isSelected ? 'athlete-item--selected' : ''}`}
                                                                    onClick={() => toggleCoachSelection(coach.id)}
                                                                >
                                                                    <div className={`athlete-avatar-sm ${isSelected ? 'athlete-avatar-sm--selected' : ''}`}>
                                                                        {coach.fullName?.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div className="athlete-info">
                                                                        <div className="athlete-name">{coach.fullName}</div>
                                                                        <div className="athlete-details">
                                                                            <span>{getEmailBase(coach.email)}</span>
                                                                            {coach.nif && <span className="athlete-nif-tag">NIF: {coach.nif}</span>}
                                                                        </div>
                                                                    </div>
                                                                    <div className={`athlete-select-indicator ${isSelected ? 'athlete-select-indicator--on' : ''}`}>
                                                                        {isSelected ? <FaTimes /> : <FaPlus />}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                        {coachesAlreadyInTeam.length > 0 && (
                                                            <div className="already-in-team-section">
                                                                <div className="already-in-team-label">
                                                                    <FaUserCheck /> Já na equipa
                                                                </div>
                                                                {coachesAlreadyInTeam.map(coach => (
                                                                    <div key={coach.id} className="athlete-item athlete-item--in-team">
                                                                        <div className="athlete-avatar-sm athlete-avatar-sm--in-team">
                                                                            {coach.fullName?.charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <div className="athlete-info">
                                                                            <div className="athlete-name">{coach.fullName}</div>
                                                                            <div className="athlete-details">
                                                                                <span>{getEmailBase(coach.email)}</span>
                                                                            </div>
                                                                        </div>
                                                                        <span className="in-team-badge">Na equipa</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right: selected panel */}
                                            <div className="add-athlete-selected-panel">
                                                <div className="teams-selected-panel-header">
                                                    <div className="selected-panel-title">
                                                        <FaUsers />
                                                        <span>Selecionados</span>
                                                        <span className="selected-panel-count">{selectedCoaches.length}</span>
                                                    </div>
                                                    {selectedCoaches.length > 0 && (
                                                        <button
                                                            className="teams-btn-submit selected-panel-header-btn"
                                                            onClick={handleAddCoaches}
                                                            disabled={submitting}
                                                        >
                                                            {submitting ? '...' : <FaUserPlus />}
                                                            <span>Adicionar</span>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="selected-panel-list">
                                                    {selectedCoaches.length === 0 ? (
                                                        <div className="selected-panel-empty">
                                                            <FaUsers className="selected-panel-empty-icon" />
                                                            <p>Clique nos treinadores para os selecionar</p>
                                                        </div>
                                                    ) : (
                                                        selectedCoaches.map(id => {
                                                            const coach = availableCoaches.find(c => c.id === id);
                                                            if (!coach) return null;
                                                            return (
                                                                <div key={id} className="selected-panel-item">
                                                                    <span className="selected-panel-name">{coach.fullName}</span>
                                                                    <button className="selected-panel-remove" onClick={(e) => { e.stopPropagation(); toggleCoachSelection(id); }} title="Remover">
                                                                        <FaTimes />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="roster-grid">
                                            {viewingTeam.coaches && viewingTeam.coaches.length > 0 ? (
                                                viewingTeam.coaches.map(coach => (
                                                    <div key={coach.userId} className="roster-card">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                                            <div className="roster-card-avatar">
                                                                {coach.name?.charAt(0)}
                                                            </div>
                                                            <div className="roster-card-info">
                                                                <span className="roster-card-name">{coach.name}</span>
                                                                <span className="roster-card-role">{coach.role || 'Treinador'}</span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="btn-remove-athlete"
                                                            onClick={() => handleRemoveCoach(coach.userId)}
                                                            title="Remover da equipa"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                ))
                                            ) : (
                                                <p style={{ color: '#999', fontStyle: 'italic', padding: '1rem' }}>Sem treinadores atribuídos.</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Athletes Section */}
                                <div className="roster-section">
                                    <h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between', width: '100%' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <FaUsers style={{ color: '#003380' }} />
                                                <span>Atletas</span>
                                                <span style={{
                                                    background: '#e3f2fd',
                                                    color: '#1976d2',
                                                    borderRadius: '20px',
                                                    padding: '2px 10px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600
                                                }}>
                                                    {viewingTeam.athletes?.length || 0}
                                                </span>
                                            </div>
                                            {!addingAthletes && (
                                                <button
                                                    className="teams-btn-submit"
                                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                                    onClick={() => { setAddingAthletes(true); fetchAvailableAthletes(); }}
                                                >
                                                    <FaUserPlus /> Adicionar Atletas
                                                </button>
                                            )}
                                            {addingAthletes && (
                                                <button
                                                    className="btn-cancel"
                                                    style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                                    onClick={() => { setAddingAthletes(false); setSelectedAthletes([]); setAthleteSearch(''); }}
                                                >
                                                    <FaTimes /> Cancelar
                                                </button>
                                            )}
                                        </div>
                                    </h3>

                                    {addingAthletes ? (
                                        <div className="add-athlete-layout">
                                            {/* Left: search + athlete list */}
                                            <div className="add-athlete-search-panel">
                                                <div className="athlete-search-bar-wrap">
                                                    <FaSearch className="athlete-search-icon" />
                                                    <input
                                                        type="text"
                                                        className="athlete-search-input"
                                                        placeholder="Pesquisar por nome, NIF ou email..."
                                                        value={athleteSearch}
                                                        onChange={(e) => setAthleteSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                    {athleteSearch && (
                                                        <button className="athlete-search-clear" onClick={() => setAthleteSearch('')} title="Limpar">
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                </div>

                                                {filteredAthletes.length > 0 && (
                                                    <div className="athlete-list-controls">
                                                        <button
                                                            className="athlete-list-ctrl-btn"
                                                            onClick={areAllFilteredSelected ? handleDeselectAllFiltered : handleSelectAllFiltered}
                                                        >
                                                            {areAllFilteredSelected ? <><FaUserCheck /> Desselecionar todos</> : <><FaUserPlus /> Selecionar todos</>}
                                                        </button>
                                                        <span className="athlete-list-count">{filteredAthletes.length} disponíveis</span>
                                                    </div>
                                                )}

                                                {athletesLoading ? (
                                                    <div className="athletes-loading">
                                                        <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }}></div>
                                                        <span>A carregar atletas...</span>
                                                    </div>
                                                ) : (
                                                    <div className="athletes-list">
                                                        {filteredAthletes.length === 0 && athletesAlreadyInTeam.length === 0 && (
                                                            <div className="no-results">
                                                                <span className="no-results-icon">🔍</span>
                                                                <p>{athleteSearch ? 'Sem resultados para a pesquisa' : 'Sem atletas disponíveis'}</p>
                                                            </div>
                                                        )}

                                                        {filteredAthletes.map(athlete => {
                                                            const isSelected = selectedAthletes.includes(athlete.athleteProfileId);
                                                            return (
                                                                <div
                                                                    key={athlete.id}
                                                                    className={`athlete-item ${isSelected ? 'athlete-item--selected' : ''}`}
                                                                    onClick={() => toggleAthleteSelection(athlete.athleteProfileId)}
                                                                >
                                                                    <div className={`athlete-avatar-sm ${isSelected ? 'athlete-avatar-sm--selected' : ''}`}>
                                                                        {athlete.fullName?.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div className="athlete-info">
                                                                        <div className="athlete-name">{athlete.fullName}</div>
                                                                        <div className="athlete-details">
                                                                            <span>{getEmailBase(athlete.email)}</span>
                                                                            {athlete.nif && <span className="athlete-nif-tag">NIF: {athlete.nif}</span>}
                                                                        </div>
                                                                    </div>
                                                                    <div className={`athlete-select-indicator ${isSelected ? 'athlete-select-indicator--on' : ''}`}>
                                                                        {isSelected ? <FaTimes /> : <FaPlus />}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                        {athletesAlreadyInTeam.length > 0 && (
                                                            <div className="already-in-team-section">
                                                                <div className="already-in-team-label">
                                                                    <FaUserCheck /> Já na equipa
                                                                </div>
                                                                {athletesAlreadyInTeam.map(athlete => (
                                                                    <div key={athlete.id} className="athlete-item athlete-item--in-team">
                                                                        <div className="athlete-avatar-sm athlete-avatar-sm--in-team">
                                                                            {athlete.fullName?.charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <div className="athlete-info">
                                                                            <div className="athlete-name">{athlete.fullName}</div>
                                                                            <div className="athlete-details">
                                                                                <span>{getEmailBase(athlete.email)}</span>
                                                                            </div>
                                                                        </div>
                                                                        <span className="in-team-badge">Na equipa</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right: selected panel */}
                                            <div className="add-athlete-selected-panel">
                                                <div className="teams-selected-panel-header">
                                                    <div className="selected-panel-title">
                                                        <FaUsers />
                                                        <span>Selecionados</span>
                                                        <span className="selected-panel-count">{selectedAthletes.length}</span>
                                                    </div>
                                                    {selectedAthletes.length > 0 && (
                                                        <button
                                                            className="teams-btn-submit selected-panel-header-btn"
                                                            onClick={handleAddAthletes}
                                                            disabled={submitting}
                                                        >
                                                            {submitting ? '...' : <FaUserPlus />}
                                                            <span>Adicionar</span>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="selected-panel-list">
                                                    {selectedAthletes.length === 0 ? (
                                                        <div className="selected-panel-empty">
                                                            <FaUsers className="selected-panel-empty-icon" />
                                                            <p>Clique nos atletas para os selecionar</p>
                                                        </div>
                                                    ) : (
                                                        selectedAthletes.map(id => {
                                                            const athlete = availableAthletes.find(a => a.athleteProfileId === id);
                                                            if (!athlete) return null;
                                                            return (
                                                                <div key={id} className="selected-panel-item">
                                                                    <span className="selected-panel-name">{athlete.fullName}</span>
                                                                    <button className="selected-panel-remove" onClick={(e) => { e.stopPropagation(); toggleAthleteSelection(id); }} title="Remover">
                                                                        <FaTimes />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="roster-grid">
                                            {viewingTeam.athletes && viewingTeam.athletes.length > 0 ? (
                                                viewingTeam.athletes.map(athlete => (
                                                    <div key={athlete.id} className="roster-card">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                                            <div className="roster-card-avatar" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                                                                {athlete.name.charAt(0)}
                                                            </div>
                                                            <div className="roster-card-info">
                                                                <span className="roster-card-name">
                                                                    {athlete.name}
                                                                    {athlete.isCaptain && <span className="captain-badge">👑</span>}
                                                                </span>
                                                                <span className="roster-card-role" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                                                    <span>{athlete.position || 'Atleta'}</span>
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                        • #
                                                                        <input
                                                                            type="number"
                                                                            defaultValue={athlete.jerseyNumber || ''}
                                                                            onBlur={(e) => {
                                                                                const val = e.target.value;
                                                                                if (val != athlete.jerseyNumber) {
                                                                                    handleUpdateJerseyNumber(viewingTeam.id, athlete.id, val);
                                                                                }
                                                                            }}
                                                                            style={{ width: '45px', padding: '2px 4px', fontSize: '0.85rem', border: '1px solid #cbd5e1', borderRadius: '4px', marginLeft: '4px' }}
                                                                            title="Editar número"
                                                                        />
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="btn-remove-athlete"
                                                            onClick={() => handleRemoveAthlete(athlete.id)}
                                                            title="Remover da equipa"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                ))
                                            ) : (
                                                <p style={{ color: '#999', fontStyle: 'italic' }}>Sem atletas na equipa.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {/* ── Attendance Modal ── */}
            {showAttendanceModal && attendanceTeam && (
                <TeamAttendanceHistoryModal
                    isOpen={showAttendanceModal}
                    onClose={() => { setShowAttendanceModal(false); setAttendanceTeam(null); }}
                    teamId={attendanceTeam.id}
                    athletes={attendanceTeam.athletes || []}
                    teamName={attendanceTeam.name}
                    sportName={attendanceTeam.sportName}
                    season={attendanceTeam.season}
                    readOnly={isCoordinator}
                />
            )}
        </div>
    );
};

/* ── TeamsTable (for history) ────────────────────────────── */
const TeamsTable = ({
    teams,
    onAdvance,
    onRevert,
    onView,
    onEdit,
    onDelete,
    onAttendance,
    getSortIcon,
    handleSort,
    getGenderBadgeClass,
    getGenderLabel
}) => (
    <div className="teams-table-container">
        <table className="teams-table">
            <thead>
                <tr>
                    <th onClick={() => handleSort('name')} className="sortable-header">Nome {getSortIcon('name')}</th>
                    <th onClick={() => handleSort('category')} className="sortable-header">Escalão {getSortIcon('category')}</th>
                    <th onClick={() => handleSort('gender')} className="sortable-header">Género {getSortIcon('gender')}</th>
                    <th>Ações</th>
                </tr>
            </thead>
            <tbody>
                {teams.map(team => (
                    <tr key={team.id}>
                        <td className="team-name" data-label="Nome">{team.name}</td>
                        <td data-label="Escalão">{team.category || '—'}</td>
                        <td data-label="Género">
                            <span className={`gender-badge ${getGenderBadgeClass(team.gender)}`}>
                                {getGenderLabel(team.gender)}
                            </span>
                        </td>
                        <td>
                            <div className="actions-cell">
                                {team.isActive && onAdvance && (
                                    <button className="teams-action-btn next-season" onClick={() => onAdvance(team)} title="Avançar Época"><FaForward /></button>
                                )}
                                {!team.isActive && onRevert && (
                                    <button className="teams-action-btn revert" onClick={() => onRevert(team)} title="Reverter Época"><FaUndo /></button>
                                )}
                                <button className="teams-action-btn view" onClick={() => onView(team.id)} title="Ver detalhes"><FaUsers /></button>
                                {onAttendance && (
                                    <button className="teams-action-btn attendance" onClick={() => onAttendance(team)} title="Quadro de Presenças"><FaClipboardCheck /></button>
                                )}
                                <button className="teams-action-btn edit" onClick={() => onEdit(team)} title="Editar"><FaEdit /></button>
                                <button className="teams-action-btn delete" onClick={() => onDelete(team.id)} title="Eliminar"><FaTrash /></button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default TeamsManager;